import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getBlogListing } from "../../store/Slice/blogListingSlice";
// import LendingTestimonial from "./LendingTestimonial";
import { getLabelDetailList } from "../../store/Slice/labelDetailSlice";
import { getSeoPageBySlug } from "../../store/Slice/seoPageSlice";
import { getReviewDetailList } from "../../store/Slice/reviewDetailSlice";
import Slider from "react-slick";
import { Helmet } from "react-helmet";
import NotFound from "../Common/NotFound";

const Landing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pageSlug } = useParams();
  
  const { labelDetailData } = useSelector((state) => state.labelDetailList);
  const { 
    seoPageData, 
    loading: seoLoading, 
    error: seoError 
  } = useSelector((state) => state.seoPage);
  
  const { blogListingData } = useSelector((state) => state.blogListing);
  const { reviewDetailData, loading: reviewLoading } = useSelector(
    (state) => state.reviewDetailList
  );
  
  const noImage = "/assets/images/no-image.png";
  const [pageStatus, setPageStatus] = useState("loading");
  const [openAccordion, setOpenAccordion] = useState(0);
  const [showAllFAQs, setShowAllFAQs] = useState(false);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  // Fetch SEO data based on the slug
  useEffect(() => {
    if (pageSlug) {
      dispatch(getSeoPageBySlug({ slug: pageSlug }));
      dispatch(getLabelDetailList({ vSlug: pageSlug }));
    }
  }, [dispatch, pageSlug]);

  // Fetch blog listing
  useEffect(() => {
    dispatch(getBlogListing());
  }, [dispatch]);

  // Fetch testimonials
  useEffect(() => {
    const payload = { eType: "Customer" };
    dispatch(getReviewDetailList(payload));
  }, [dispatch]);

  // Validate page slug against SEO data
  useEffect(() => {
    if (seoLoading) {
      setPageStatus("loading");
      return;
    }

    if (seoError) {
      setPageStatus("notfound");
      return;
    }

    if (seoPageData) {
      if (seoPageData.code === 200 && seoPageData.data) {
        const seoData = seoPageData.data;
        
        if (seoData.vUrlFlag) {
          const urlFlagSlug = seoData.vUrlFlag.replace(/^\//, '').toLowerCase();
          const currentSlug = pageSlug.toLowerCase();
          
          if (urlFlagSlug === currentSlug) {
            setPageStatus("valid");
          } else {
            setPageStatus("notfound");
          }
        } else {
          if (seoData.vSecOneTitle || seoData.vMetaTitle) {
            setPageStatus("valid");
          } else {
            setPageStatus("notfound");
          }
        }
      } else {
        setPageStatus("notfound");
      }
    } else {
      setPageStatus("notfound");
    }
  }, [seoPageData, seoLoading, seoError, pageSlug]);

  /**
   * =========================
   * LABEL HANDLER
   * =========================
   */
  const getLabel = React.useCallback(
    (key) => {
      if (labelDetailData?.code !== 200) return "";
      return (
        labelDetailData.data.find((item) => item.vLabel === key)?.tText || ""
      );
    },
    [labelDetailData]
  );

  // const testimonialTitle =
  //   getLabel("LBL_MOVE_OUT_MOVE_IN_CLEANING_TESTIMONIAL_HEADING") ||
  //   getLabel("LBL_DEEP_CLEANING_TESTIMONIALS_HEADING") ||
  //   getLabel("LBL_POST_EVENT_CLEANING_TESTIMONIALS_HEADING") ||
  //   getLabel("LBL_ROUTINE_ONE_TIME_CLEANING_TESTIMONIALS_HEADING") ||
  //   getLabel("LBL_MOVE_OUT_IN_CLEANING_EDMONTON_TESTIMONIAL_SECTION_TITLE") ||
  //   "What Our Customers Say";

  /**
   * =========================
   * SLIDER SETTINGS
   * =========================
   */
  const testimonialSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          autoplay: true,
          adaptiveHeight: true,
        },
      },
    ],
  };

  /**
   * =========================
   * TESTIMONIAL DATA CHECK
   * =========================
   */
  const hasTestimonials =
    reviewDetailData?.code === 200 &&
    Array.isArray(reviewDetailData?.data) &&
    reviewDetailData.data.length > 0;

  /**
   * =========================
   * FORCE SLIDER REMOUNT
   * =========================
   */
  const sliderKey = hasTestimonials ? reviewDetailData.data.length : 0;

  // Show loading state
  if (pageStatus === "loading") {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        {/* <div>Loading...</div> */}
      </div>
    );
  }

  // Show 404 page if slug doesn't match
  if (pageStatus === "notfound") {
    return <NotFound />;
  }

  const handleBlogClick = (blogId, blogSlug) => {
    navigate(`/blog-detail/${blogSlug}`, {
      state: { blogId },
    });
  };

  const handleBookNowClick = () => {
    navigate("/book-now");
  };

  // SEO Data Extraction
  const seoData = seoPageData?.data;
  
  const bufferToString = (bufferObj) => {
    if (!bufferObj || !bufferObj.data) return "";
    return new TextDecoder("utf-8").decode(new Uint8Array(bufferObj.data));
  };

  // Helper function to get complete image URL
  const getImageUrl = (imageName) => {
    if (!imageName) return "";
    return `${process.env.REACT_APP_ADMIN_URL}${imageName}`;
  };

  const secTwoDescription = seoData?.vSecTwoDescription?.data
    ? bufferToString(seoData.vSecTwoDescription)
    : "";

  const secFiveDesc = seoData?.vSecFiveDesc?.data
    ? bufferToString(seoData.vSecFiveDesc)
    : "";

  const secSixDesc = seoData?.vSecSixDescOne?.data
    ? bufferToString(seoData.vSecSixDescOne)
    : "";

  const secEighDesc = seoData?.vSecEightDesc?.data
    ? bufferToString(seoData.vSecEightDesc)
    : "";

  // Filter transactions for different sections
  const secEightData = seoData?.seo_page_transactions?.filter(
    (item) => item.vSectionName === "SecEight"
  ) || [];

  const secNineData = seoData?.seo_page_transactions?.filter(
    (item) => item.vSectionName === "SecNine"
  ) || [];

  // Filter FAQ transactions
  const faqData = seoData?.seo_page_transactions?.filter(
    (item) => item.vSectionName === "SecFAQ"
  ) || [];

  // ========== SEO META DATA ==========
  // Get ALL SEO fields with proper fallbacks using YOUR database field names
  const metaTitle = seoData?.vMetaTitle || 
                   seoData?.vSecOneTitle || 
                   `SEO Page - ${pageSlug}`;

  // Meta Description
  const metaDescription = seoData?.vMetaDescription || 
                         (seoData?.vSecOneDesc?.data ? bufferToString(seoData.vSecOneDesc)?.substring(0, 160) : "") ||
                         "Professional cleaning services by MopForce.";

  const metaKeywords = seoData?.vMetaKeyword || "";

  // Get complete canonical URL
  const baseUrl = window.location.origin;
  const canonicalUrl = seoData?.vOgUrl || `${baseUrl}/${pageSlug}`;

  // Open Graph Data - using YOUR field names
  const ogTitle = seoData?.vOgTitle || metaTitle;

  // OG Description - handle buffer format
  const ogDescription = seoData?.tOgDescription ? bufferToString(seoData.tOgDescription) : metaDescription;

  // OG Image - get complete URL
  const ogImage = seoData?.vOgImage ? 
                 `${process.env.REACT_APP_ADMIN_URL}${seoData.vOgImage}` : 
                 seoData?.vSecOneImage ? 
                 `${process.env.REACT_APP_ADMIN_URL}${seoData.vSecOneImage}` : 
                 `${baseUrl}/assets/images/logo.png`;

  // Twitter Card Data (use OG data as fallback)
  const twitterCard = "summary_large_image";
  const twitterTitle = metaTitle;
  const twitterDescription = metaDescription;
  const twitterImage = ogImage;

  // Robots meta tag
  const robotsMeta = "index, follow";

  // Additional meta tags
  const siteName = "MopForce";
  const currentUrl = window.location.href;

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div>
      {/* ========== SEO META TAGS ========== */}
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        <meta name="robots" content={robotsMeta} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={twitterDescription} />
        <meta name="twitter:image" content={twitterImage} />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="MopForce" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* Banner Section */}
      <section
        className="ptb-100 home-bannersection"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <img
          alt={
          seoData?.vSecOneImage 
            ? (seoData?.vSecOneImageAlt || "Image")  // Fallback if alt text is empty
            : "Image"
        }
          fetchpriority="high"
          decoding="async"
          loading="eager"
          src={
            seoData?.vSecOneImage
              ? getImageUrl(seoData.vSecOneImage)
              : ""
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: "0px",
            left: "0px",
            zIndex: "-1",
            filter: "none",
            transition: "filter 0.3s ease-out",
          }}
        />

        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7 col-md-12">
              <div className="banner-content">
                <h1 className="banner-title">
                  <span>
                    {seoData?.vSecOneTitle || "Move In/Out Cleaning in Edmonton - Book Anytime, Day or Night"}
                  </span>
                </h1>

                <div
                  dangerouslySetInnerHTML={{
                    __html: seoData?.vSecOneDesc?.data
                      ? bufferToString(seoData.vSecOneDesc)
                      : "Whether you're settling into a new place or saying goodbye to an old one, our Edmonton-based team is available day or night to deliver the deep clean you need.",
                  }}
                />

                <button
                  className="success-orangebtn"
                  type="button"
                  onClick={handleBookNowClick}
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Now Button */}
      <div className="landing-blogbtnDiv">
        <button className="success-orangebtn" onClick={handleBookNowClick}>
          Book Now
        </button>
      </div>

      {/* About Section */}
      <section className="aboutus-section pt-100">
        <div className="container">
          <div className="row align-items-center responsive-aboutrow">
            <div className="col-lg-12 text-center">
              <h2 className="main-title">
                {seoData?.vSecTwoSubHeading || "About This Service"}
              </h2>
              {secTwoDescription && (
                <div
                  className="gray-para"
                  dangerouslySetInnerHTML={{ __html: secTwoDescription }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Landing About Section */}
      <section className="landing-aboutsection ptb-100">
        <div className="container">
          <div className="landing-aboutcard">
            <div className="row justify-content-between">
              <div className="col-lg-7">
                <div className="landingabout-contentDiv">
                  <h2 className="howitwork-title mb-30">
                    {seoData?.vSecFivTitle}
                  </h2>
                  {secFiveDesc && (
                    <div
                      className="gray-para"
                      dangerouslySetInnerHTML={{ __html: secFiveDesc }}
                    />
                  )}
                </div>
              </div>
              <div className="col-lg-5">
                <div className="landingabout-imgDiv">
                  <img
                    className="landing-aboutimg img-fluid h-100"
                    src={
                      seoData?.vSecFiveImage
                        ? getImageUrl(seoData.vSecFiveImage)
                        : ""
                    }
                    width="470"
                    height="425"
                    alt={
                      seoData?.vSecFiveImage 
                        ? (seoData?.section2ImageAlt || "Image")
                        : "Image"
                    }
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="landing-servicesection ptb-100">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-12">
              <h2 className="howitwork-title mb-30">
                {seoData?.vSecEightTitle}
              </h2>
              {secEighDesc && (
                <div
                  className="gray-para"
                  dangerouslySetInnerHTML={{ __html: secEighDesc }}
                />
              )}
            </div>

            <div className="row">
              {secEightData.map((item) => (
                <div
                  className="col-xl-3 col-lg-4 col-md-6"
                  key={item.iSeoPageTranId}
                >
                  <div className="landing-service-card">
                    <img
                      src={`${process.env.REACT_APP_ADMIN_URL}${item.iSeoPageId}/${item.vImage}`}
                      alt={item.vImage ? item.vTitle : "icon"}
                      className="landing-service-icon"
                      loading="lazy"
                    />
                    <h3 className="landing-servicetitle">
                      {item.vTitle}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Second About Section */}
      <section className="landing-aboutsection ptb-100">
        <div className="container pb-50">
          <div className="landing-aboutcard">
            <div className="row justify-content-between">
              <div className="col-lg-7">
                <div className="landingabout-contentDiv">
                  <h2 className="howitwork-title mb-30">
                    {seoData?.vSecSixTitle}
                  </h2>
                  {secSixDesc && (
                    <div
                      className="gray-para"
                      dangerouslySetInnerHTML={{ __html: secSixDesc }}
                    />
                  )}
                </div>
              </div>
              <div className="col-lg-5">
                <div className="landingabout-imgDiv">
                  <img
                    className="landing-aboutimg img-fluid h-100"
                    src={
                      seoData?.vSecSixImage
                        ? getImageUrl(seoData.vSecSixImage)
                        : "/assets/images/landing/edmonton-clean-client-home.png"
                    }
                    width="470"
                    height="425"
                    alt={
                      seoData?.vSecSixImage 
                        ? (seoData?.section3ImageAlt || "Image")
                        : "Image"
                    }
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="aboutus-section ptb-100">
        <div className="container">
          <div className="row align-items-center responsive-aboutrow">
            <div className="col-lg-12">
              <h2 className="main-title">
                {seoData?.vSecNineTitle}
              </h2>
            </div>
          </div>

          <div className="row g-lg-4 g-3">
            {secNineData.map((item, index) => (
              <div className="col-lg-6 col-md-6" key={index}>
                <div className="deepclean-cardDiv">
                  <img
                    className="serviceclening-img"
                    src={`${process.env.REACT_APP_ADMIN_URL}${item.iSeoPageId}/${item.vImage}`}
                    width="2000"
                    height="1333"
                    alt={item.vImage ? item.vTitle : "icon"}
                    loading="lazy"
                  />
                  <div>
                    <h3 className="medium-title">{item.vTitle}</h3>
                    {item.tDescription && (
                      <p
                        className="mb-0"
                        dangerouslySetInnerHTML={{ __html: item.tDescription }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section
        className="testimonial-section testimonial-landingsection ptb-100"
        style={{ backgroundImage: "url('/assets/images/service-bg.webp')" }}
      >
        <div className="container position-relative z-1">
          <div className="row align-items-center mb-30">
            <div className="col-lg-9">
              <h2 className="main-title white-title">{seoData.vSecSevenTitle}</h2>
            </div>
          </div>

          {!reviewLoading && hasTestimonials ? (
            <Slider
              key={sliderKey}
              {...testimonialSettings}
              className="testimonial-slider servicedetail-slider landing-testimonialslider"
            >
              {reviewDetailData.data.map((review, index) => (
                <div className="testimonial-card" key={index}>
                  <div className="testimonial-imgcontent">
                    <h3 className="howitwork-title mb-0">
                      {review?.vName || "Customer"}
                    </h3>
                  </div>

                  <p>{review?.tDescription}</p>

                  <div className="rating-quoteDiv">
                    <span
                      className="star-ratings-css"
                      title={review?.iRating}
                    >
                      <i
                        style={{
                          width: `${(review?.iRating / 5) * 100}%`,
                        }}
                      ></i>
                    </span>

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="88"
                      height="68"
                      viewBox="0 0 88 68"
                      fill="none"
                    >
                      <g opacity="0.8">
                        <path
                          d="M8.88562 0.824215L5.0284 6.38403C11.9796 9.99821..."
                          stroke="#E1E1E1"
                        />
                        <path
                          d="M58.3599 0.728767L54.4209 6.40189C65.2553 12.5..."
                          stroke="#E1E1E1"
                        />
                      </g>
                    </svg>
                  </div>
                </div>
              ))}
            </Slider>
          ) : (
            !reviewLoading && (
              <div className="col-12">
                <p className="text-center text-white">No Testimonials Found</p>
              </div>
            )
          )}
        </div>
      </section>
      
      {/* Blogs Section */}
      <section className="landingblog-section ptb-100">
        <div className="container">
          <div className="row align-items-center g-3 g-sm-4">
            <div className="col-lg-9">
              <h2 className="main-title">Blogs</h2>
            </div>
            {blogListingData &&
              blogListingData?.code === 200 &&
              blogListingData?.data &&
              [...blogListingData.data]
                ?.sort(
                  (a, b) => new Date(b.dtAddedDate) - new Date(a.dtAddedDate)
                )
                ?.slice(0, 3)
                .map((blogs, bolgsIndex) => (
                  <div className="col-xl-4 col-lg-6 col-md-6" key={bolgsIndex}>
                    <div className="blog-maincard">
                      <div className="blog-cardimgDiv">
                        <img
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            handleBlogClick(blogs.iBlogId, blogs.vSlug)
                          }
                          className="blog-cardimg"
                          width={307}
                          height={200}
                          src={
                            blogs?.vWebpImage &&
                            !blogs?.vWebpImage.endsWith(null)
                              ? blogs?.vWebpImage
                              : noImage
                          }
                          alt={blogs?.vImageAlt}
                          loading="lazy"
                        />
                        <span
                          className="blogviewdate"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            handleBlogClick(blogs.iBlogId, blogs.vSlug)
                          }
                        >
                          {formatDate(blogs?.dtAddedDate)}
                        </span>
                      </div>
                      <div className="blogcard-content">
                        <div className="blogtype-dateDiv">
                          <span
                            className="blogclean-type"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              handleBlogClick(blogs.iBlogId, blogs.vSlug)
                            }
                          >
                            {blogs.category?.vCategory}
                          </span>
                        </div>
                        <div
                          className="text-decoration-none"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            handleBlogClick(blogs.iBlogId, blogs.vSlug)
                          }
                        >
                          <h2 className="medium-title">{blogs.vBlog}</h2>
                        </div>

                        <div className="blog-cont">
                          <p
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              handleBlogClick(blogs.iBlogId, blogs.vSlug)
                            }
                            dangerouslySetInnerHTML={{
                              __html: blogs.tDescription,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Dynamic FAQ Section with View All functionality */}
      <section className="frequestion-section pb-100">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 text-center">
              <h2 className="main-title">Frequently Asked Questions</h2>
            </div>
          </div>

          <div className="frequently-questioncard">
            {faqData.length > 0 ? (
              <>
                <div className="accordion" id="accordionExample">
                  {faqData
                    .slice(0, showAllFAQs ? faqData.length : 5)
                    .map((faqItem, index) => (
                      <div className="accordion-item" key={faqItem.iSeoPageTranId || faqItem.vUniqueCode || index}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${openAccordion === index ? '' : 'collapsed'}`}
                            type="button"
                            onClick={() => toggleAccordion(index)}
                            aria-expanded={openAccordion === index ? 'true' : 'false'}
                          >
                            {faqItem.vTitle || `Question ${index + 1}`}
                          </button>
                        </h2>
                        <div
                          className={`accordion-collapse collapse ${openAccordion === index ? 'show' : ''}`}
                        >
                          <div className="accordion-body">
                            {faqItem.tDescription ? (
                              <div dangerouslySetInnerHTML={{ __html: faqItem.tDescription }} />
                            ) : faqItem.vSubTitle ? (
                              <div>{faqItem.vSubTitle}</div>
                            ) : (
                              <div>No answer available for this question.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                
                {/* Show View All button only if there are more than 5 FAQs AND we're not already showing all */}
                {faqData.length > 5 && !showAllFAQs && (
                  <div className="text-center mt-4">
                    <button 
                      className="success-orangebtn"
                      onClick={() => {
                        setShowAllFAQs(true);
                        // Keep first FAQ open when clicking View All
                        if (openAccordion === null) {
                          setOpenAccordion(0);
                        }
                      }}
                    >
                      View All Questions ({faqData.length})
                    </button>
                  </div>
                )}

                {/* Optional: Show Show Less button when all FAQs are shown */}
                {faqData.length > 5 && showAllFAQs && (
                  <div className="text-center mt-4">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAllFAQs(false);
                        setOpenAccordion(0); // Reset to first tab when showing less
                      }}
                      style={{
                        backgroundColor: '#6c757d',
                        borderColor: '#6c757d',
                        padding: '10px 30px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '5px'
                      }}
                    >
                      Show Less
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <p>No frequently asked questions available at the moment.</p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section
        className="lastdetails-section ptb-100"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <img
          alt="Clean, empty living room in Edmonton home"
          fetchpriority="high"
          decoding="async"
          loading="eager"
          src="/assets/images/landing/edmonton-clean-home-cta.png"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: "0px",
            left: "0px",
            zIndex: "-1",
            filter: "none",
            transition: "filter 0.3s ease-out",
          }}
        />
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-9 col-md-12">
              <div className="about-contentDiv">
                <h2 className="main-title">
                  {seoData?.vLastSecTitle}
                </h2>
                <p>
                  {seoData?.tLastSecDescription}
                </p>
                <button
                  className="success-orangebtn"
                  type="button"
                  onClick={handleBookNowClick}
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </div>

        <img
          className="img-fluid last-details-shape"
          src="/assets/images/last-details-shapeimg.webp"
          width="564"
          height="628"
          alt="Shape"
          loading="lazy"
        />
      </section>
    </div>
  );
};

export default Landing;